import React, { ReactElement, useEffect } from "react";
import Head from "next/head";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "#next/components/ui/card";
import { Button } from "#next/components/ui/button";
import { Input } from "#next/components/ui/input";
import { useIsUserLoggedIn } from "#next/hooks/use-is-user-logged-in";
import { useRouter } from "next/router";
import { useUser } from "#next/hooks/use-user";
import { UsageLimitsDisplay } from "#next/app-components/usage-limits-display";
import { useApiToken } from "#next/hooks/use-api-token";
import { SubscriptionManagement } from "#next/components/stripe/subscription-management";
import { Header } from "#next/app-components";
import { UTCDate } from "@date-fns/utc";

export default function Settings(): ReactElement {
  const router = useRouter();
  const [ tokenCopied, setTokenCopied ] = React.useState(false);
  const { isLoggedIn, isLoading: isLoggedInLoading } = useIsUserLoggedIn();

  // Redirect to home page if not authenticated
  useEffect(() => {
    console.log("Settings page: isLoggedIn", isLoggedIn, "isLoggedInLoading", isLoggedInLoading);
    if (isLoggedIn === false && !isLoggedInLoading) {
      console.log("pushed");
      // router.push("/");
    }
  }, [ isLoggedIn, isLoggedInLoading, router ]);

  const apiToken = useApiToken();

  const {
    data: user
  } = useUser();

  // Copy token to clipboard
  const copyToClipboard = async () => {
    if (apiToken) {
      await navigator.clipboard.writeText(apiToken);
      setTokenCopied(true);
      setTimeout(() => setTokenCopied(false), 2000);
    }
  };

  // If user is not logged in, return null (will be redirected to home page)
  if (isLoggedIn !== true) {
    return <></>;
  }

  return (
    <div className="flex min-h-screen flex-col bg-gradient-to-br from-slate-50 to-blue-50">
      <Header />
      <Head>
        <title>Dom Ghost | Settings</title>
        <meta name="description" content="Manage your Dom Ghost settings and account preferences" />
        <link rel="icon" href="/favicon.ico" />
      </Head>

      {/* Main Content */}
      <main className="flex-grow container mx-auto px-6 py-10">
        {/* Animated background elements */}
        <div className="fixed inset-0 z-0 overflow-hidden">
          <div className="absolute -bottom-64 -left-32 w-96 h-96 bg-purple-400 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob"></div>
          <div className="absolute -bottom-32 left-48 w-96 h-96 bg-yellow-300 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob animation-delay-2000"></div>
          <div className="absolute top-32 -right-32 w-96 h-96 bg-indigo-500 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob animation-delay-4000"></div>
        </div>

        <div className="grid grid-cols-1 gap-8 relative z-10">
          {/* Settings Content */}
          <div className="max-w-4xl mx-auto w-full">
            <Card className="bg-white/80 backdrop-blur-md border border-indigo-100/50 shadow-2xl rounded-2xl overflow-hidden transition-all hover:shadow-indigo-200/30 hover:bg-white/90">
              <CardHeader className="bg-gradient-to-r from-indigo-600 via-violet-600 to-purple-600 text-white p-6 relative">
                <div className="absolute inset-0 bg-grid-white/5 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSIgZD0iTTAgMGgzMHYzMEgwem0zMCAzMGgzMHYzMEgzMHoiLz48L2c+PC9zdmc+')] opacity-30"></div>

                {/* Animated particles */}
                <div className="absolute inset-0 overflow-hidden">
                  {[ ...Array(10) ].map((_, i) => (
                    <div
                      key={i}
                      className="absolute rounded-full bg-white/10 animate-float"
                      style={{
                        width: `${Math.random() * 8 + 4}px`,
                        height: `${Math.random() * 8 + 4}px`,
                        top: `${Math.random() * 100}%`,
                        left: `${Math.random() * 100}%`,
                        animationDuration: `${Math.random() * 10 + 10}s`,
                        animationDelay: `${Math.random() * 5}s`
                      }}
                    />
                  ))}
                </div>

                <div className="relative z-10">
                  <div className="flex items-center">
                    <div className="flex justify-center items-center p-3 bg-indigo-500/50 backdrop-blur-sm rounded-xl shadow-lg mr-4 transform transition-transform duration-500 hover:rotate-12 hover:scale-110">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="text-white drop-shadow">
                        <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"/>
                        <circle cx="12" cy="12" r="3"/>
                      </svg>
                    </div>
                    <div>
                      <CardTitle className="text-2xl font-bold mb-1 drop-shadow-md">Settings</CardTitle>
                      <CardDescription className="text-indigo-100 text-base">
                        Manage your Dom Ghost account and API token
                      </CardDescription>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-7 px-7">
                <div className="flex flex-col space-y-6">


                  {/* Usage Limits Display */}
                  <UsageLimitsDisplay />

                  {/* Divider */}
                  <div className="mt-6 border-t border-indigo-100"></div>

                  <div className="bg-gradient-to-b from-white/80 to-indigo-50/50 p-6 rounded-xl border border-indigo-100 shadow-lg backdrop-blur-sm">
                    <div className="flex items-start gap-4 mb-5">
                      <div className="mt-0.5 text-indigo-600 bg-indigo-100 rounded-full p-2 flex-shrink-0 shadow transform transition-transform hover:rotate-12 hover:scale-110 duration-300">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg>
                      </div>
                      <div>
                        <p className="text-indigo-700 font-medium leading-relaxed">
                          Your API token is required for both the browser extension and Dom Ghost server to authenticate requests securely.
                        </p>
                        <p className="text-indigo-500 text-sm mt-1">Keep your token private and do not share it with others.</p>
                      </div>
                    </div>

                    <div className="flex flex-col space-y-3 sm:flex-row sm:space-y-0 sm:space-x-3">
                      <div className="relative flex-grow group">
                        <div className="absolute inset-0 bg-gradient-to-r from-indigo-400/20 via-violet-400/20 to-purple-400/20 rounded-lg blur-md transition-opacity opacity-0 group-hover:opacity-100"></div>
                        <Input
                          value={apiToken || "No token generated"}
                          readOnly
                          className="font-mono text-gray-800 border-2 border-indigo-200 bg-white/90 focus:border-indigo-400 focus:ring-2 focus:ring-indigo-300 shadow-md h-12 pl-4 pr-8 rounded-lg relative z-10 transition-all select-all"
                          onClick={(e) => (e.target as HTMLInputElement).select()}
                        />
                        {apiToken && (
                          <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none z-20">
                            <div className="h-3 w-3 rounded-full bg-green-500 animate-pulse shadow-lg shadow-green-200"></div>
                          </div>
                        )}
                      </div>
                      <Button
                        variant="outline"
                        onClick={copyToClipboard}
                        disabled={!apiToken}
                        className="bg-white border-2 border-indigo-300 text-indigo-700 hover:bg-indigo-50 hover:border-indigo-400 shadow-md hover:shadow-lg font-semibold h-12 min-w-[110px] rounded-lg transition-all duration-300 hover:scale-105 group"
                      >
                        {tokenCopied ? (
                          <span className="flex items-center gap-2">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="18"
                              height="18"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              className="text-green-500"><path d="M20 6 9 17l-5-5"/></svg>
                            <span>Copied!</span>
                          </span>
                        ) : (
                          <span className="flex items-center gap-2">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="18"
                              height="18"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              className="group-hover:rotate-12 transition-transform duration-300"><rect
                                width="14"
                                height="14"
                                x="8"
                                y="8"
                                rx="2"
                                ry="2"/><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/></svg>
                            <span>Copy</span>
                          </span>
                        )}
                      </Button>
                    </div>
                    {tokenCopied && (
                      <div className="mt-4 p-3 bg-green-50/90 border border-green-200 rounded-lg shadow-md animate-fadeIn backdrop-blur-sm">
                        <p className="text-sm text-green-600 font-medium flex items-center gap-2">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="text-green-500"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
                          Token copied to clipboard! Ready to use with your MCP installation.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
                <div className="mt-6 pt-6 border-t border-indigo-100"></div>

                {/* Subscription Management */}
                <SubscriptionManagement />
                {/*{user && apiToken && <UserDomainInput user={user} apiToken={apiToken}/>}*/}
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-gradient-to-r from-indigo-100 via-violet-100 to-purple-100 border-t border-indigo-200 py-8 shadow-inner relative overflow-hidden mt-12">
        <div className="container mx-auto px-6 relative">
          <div className="text-center">
            <p className="text-indigo-700 font-medium">© {new UTCDate().getFullYear()} <span className="bg-clip-text text-transparent bg-gradient-to-r from-indigo-600 via-violet-600 to-purple-600 font-bold">Dom Ghost</span> • All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}